import { Group } from 'three';
import type { AnnotationLayer } from '../render/annotations';
import { createTextSprite, disposeTextSprite, type TextSprite } from '../render/textSprite';
import type { SpeechBubble } from './types';
import { translate } from '../ui/i18n';

function linesOf(text: string, context: CanvasRenderingContext2D): string[] {
  const lines: string[] = [];
  let line = '';
  for (const word of text.split(/\s+/)) {
    if (context.measureText(`${line} ${word}`.trim()).width <= 380) {
      line = `${line} ${word}`.trim();
      continue;
    }
    if (line) lines.push(line);
    line = '';
    for (const character of word) {
      if (context.measureText(line + character).width > 380) {
        lines.push(line);
        line = '';
      }
      line += character;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function speechSprite(text: string, layer: AnnotationLayer, whisper: boolean): TextSprite {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Speech rendering requires a canvas context');
  const font = `${whisper ? 'italic ' : ''}28px "EB Garamond", Georgia, serif`;
  context.font = font;
  const lines = linesOf(text, context);
  canvas.width = Math.max(
    128,
    Math.ceil(Math.max(...lines.map((line) => context.measureText(line).width))) + 32
  );
  const heading = whisper ? 24 : 0;
  canvas.height = lines.length * 36 + 32 + heading;
  context.fillStyle = '#cdb58b';
  context.strokeStyle = '#947849';
  context.lineWidth = 2;
  if (whisper) context.setLineDash([5, 4]);
  context.beginPath();
  context.roundRect(1, 1, canvas.width - 2, canvas.height - 14, 10);
  context.fill();
  context.stroke();
  context.beginPath();
  context.moveTo(canvas.width / 2 - 8, canvas.height - 14);
  context.lineTo(canvas.width / 2, canvas.height - 1);
  context.lineTo(canvas.width / 2 + 8, canvas.height - 14);
  context.fill();
  context.stroke();
  context.font = font;
  context.fillStyle = '#25190e';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  lines.forEach((line, i) => {
    context.fillText(line, canvas.width / 2, 25 + heading + i * 36);
  });
  if (whisper) {
    context.font = '20px "EB Garamond", Georgia, serif';
    context.fillText(translate('Whisper'), canvas.width / 2, 18);
  }
  return createTextSprite(canvas, lines.length * 0.17 + 0.17 + (whisper ? 0.12 : 0), layer, 0.55);
}

class SpeechLabels {
  private readonly group = new Group();
  private readonly labels = new Map<
    string,
    { utterance: string; sprite: TextSprite; bubble: SpeechBubble }
  >();

  constructor(private readonly layer: AnnotationLayer) {
    this.group.name = 'spoken-words';
    layer.scene.add(this.group);
  }

  update(bubbles: readonly SpeechBubble[]): void {
    const offsets = new Map<string, number>();
    const current = new Set(bubbles.map((bubble) => bubble.id));
    for (const [id, label] of this.labels)
      if (!current.has(id)) {
        this.group.remove(label.sprite);
        disposeTextSprite(label.sprite);
        this.labels.delete(id);
      }
    for (const bubble of bubbles) {
      let label = this.labels.get(bubble.id);
      if (!label || label.utterance !== bubble.utterance) {
        if (label) {
          this.group.remove(label.sprite);
          disposeTextSprite(label.sprite);
        }
        label = {
          utterance: bubble.utterance,
          sprite: speechSprite(bubble.text, this.layer, bubble.kind === 'whisper'),
          bubble,
        };
        this.labels.set(bubble.id, label);
        this.group.add(label.sprite);
      }
      label.sprite.position.set(...bubble.position);
      label.bubble = bubble;
      const above = bubble.stack ? (offsets.get(bubble.stack) ?? 0) : 0;
      label.sprite.position.y += above + label.sprite.scale.y / 2;
      if (bubble.stack) offsets.set(bubble.stack, above + label.sprite.scale.y + 0.05);
    }
  }

  diagnostics() {
    return [...this.labels].map(([id, label]) => ({
      id,
      text: label.bubble.text,
      kind: label.bubble.kind ?? 'say',
      speaker: label.bubble.stack ?? label.bubble.id,
      position: label.sprite.position.toArray(),
    }));
  }

  dispose(): void {
    for (const label of this.labels.values()) disposeTextSprite(label.sprite);
    this.labels.clear();
    this.group.clear();
    this.layer.scene.remove(this.group);
  }
}

export function createSpeechLabels(layer: AnnotationLayer) {
  return new SpeechLabels(layer);
}
